/**
 * SmsComposeModal — Free-form outbound SMS / MMS
 * Allows sending a text (or image/PDF attachment) to ANY US phone number.
 * Supports drag-and-drop and file picker for MMS attachments.
 * Phone field has autocomplete via searchContacts tRPC procedure.
 */
import { useState, useRef, useCallback, useEffect } from "react";
import { X, Send, Phone, Paperclip, FileText, ImageIcon, Search } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface SmsComposeModalProps {
  open: boolean;
  onClose: () => void;
  senderName: string;
  senderEmail: string;
  /** Pre-fill the recipient phone number */
  initialPhone?: string;
  /** Pre-fill the recipient name/label */
  initialName?: string;
  /** User role — tax_division → HRBBS number, others → APXGS number */
  userRole?: string;
}

/** Twilio A2P 10DLC compliance: append to every outbound message */
const OPT_OUT_FOOTER = "\n\nReply STOP to opt-out.";

/** Normalize a phone input to E.164 format (+1XXXXXXXXXX) for US numbers */
function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (raw.trim().startsWith("+")) return `+${digits}`;
  return digits;
}

/** Basic US phone validation */
function isValidUsPhone(raw: string): boolean {
  const digits = raw.replace(/\D/g, "");
  return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
}

/** Format phone for display */
function formatPhoneDisplay(raw: string): string {
  const digits = raw.replace(/\D/g, "").replace(/^1/, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

export function SmsComposeModal({ open, onClose, senderName, senderEmail, initialPhone = "", initialName = "", userRole }: SmsComposeModalProps) {
  const fromLabel: "APXGS" | "HRBBS" = userRole === "tax_division" ? "HRBBS" : "APXGS";
  const [toPhone, setToPhone] = useState(initialPhone);
  const [toName, setToName] = useState(initialName);
  const [message, setMessage] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [attachment, setAttachment] = useState<{ file: File; previewUrl: string | null } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const utils = trpc.useUtils();
  const uploadMmsFile = trpc.communications.uploadMmsFile.useMutation();

  // Contact search — fires when user types in phone field
  const { data: contactSuggestions = [] } = trpc.clientNotifications.searchContacts.useQuery(
    { query: searchQuery },
    { enabled: searchQuery.length >= 2, staleTime: 10_000 }
  );

  const sendSms = trpc.communications.sendSms.useMutation({
    onSuccess: () => {
      toast.success(`${attachment ? "MMS" : "SMS"} sent to ${toName || formatPhoneDisplay(toPhone)}`);
      utils.clientNotifications.smsCombined.invalidate();
      handleClose();
    },
    onError: (err) => {
      toast.error(err.message);
      setIsSending(false);
    },
  });

  const handleClose = () => {
    setToPhone(initialPhone);
    setToName(initialName);
    setMessage("");
    setPhoneError("");
    setAttachment(null);
    setIsSending(false);
    setShowSuggestions(false);
    setSearchQuery("");
    onClose();
  };

  const handlePhoneChange = (val: string) => {
    setToPhone(val);
    setSearchQuery(val);
    if (phoneError) setPhoneError("");
    setShowSuggestions(val.length >= 2);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        phoneInputRef.current && !phoneInputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectContact = (contact: { name: string; phone: string }) => {
    setToPhone(contact.phone);
    setToName(contact.name);
    setSearchQuery("");
    setShowSuggestions(false);
    if (phoneError) setPhoneError("");
  };

  const handleAttachment = useCallback((file: File) => {
    const MAX = 4.5 * 1024 * 1024;
    if (file.size > MAX) { toast.error("File must be under 4.5 MB for MMS"); return; }
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) { toast.error("Only JPEG, PNG, GIF, WebP, and PDF files are supported"); return; }
    const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
    setAttachment({ file, previewUrl });
  }, []);

  const handleSend = async () => {
    if (!toPhone.trim()) { setPhoneError("Phone number is required"); return; }
    if (!isValidUsPhone(toPhone)) { setPhoneError("Please enter a valid 10-digit US phone number"); return; }
    if (!message.trim() && !attachment) { toast.error("Please enter a message or attach a file"); return; }

    const normalized = normalizePhone(toPhone);
    const label = toName.trim() || formatPhoneDisplay(toPhone);
    setIsSending(true);

    try {
      let mediaUrl: string | undefined;
      if (attachment) {
        const base64 = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res((reader.result as string).split(",")[1]);
          reader.onerror = rej;
          reader.readAsDataURL(attachment.file);
        });
        const uploaded = await uploadMmsFile.mutateAsync({
          name: attachment.file.name,
          mimeType: attachment.file.type,
          size: attachment.file.size,
          fileDataBase64: base64,
        });
        mediaUrl = uploaded.url;
      }

      // Append opt-out footer per Twilio A2P 10DLC requirements
      const messageBody = message.trim()
        ? message.trim() + OPT_OUT_FOOTER
        : OPT_OUT_FOOTER.trim();
      sendSms.mutate({
        senderName,
        senderEmail,
        clientName: label,
        clientPhone: normalized,
        message: messageBody,
        mediaUrl,
        mediaFileName: attachment?.file.name,
        fromLabel,
      });
    } catch (err: unknown) {
      toast.error(`Upload failed: ${err instanceof Error ? err.message : String(err)}`);
      setIsSending(false);
    }
  };

  const charCount = message.length;
  const segmentCount = Math.ceil(charCount / 160) || 1;
  const canSend = (toPhone.trim() && (message.trim() || attachment)) && !isSending;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "oklch(0.08 0.02 240 / 70%)" }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl shadow-2xl"
        style={{
          background: "oklch(0.16 0.04 240)",
          border: `1px solid ${isDragOver ? "oklch(0.55 0.18 240)" : "oklch(0.28 0.04 240)"}`,
          transition: "border-color 0.15s",
        }}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          const file = e.dataTransfer.files[0];
          if (file) handleAttachment(file);
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 rounded-t-2xl"
          style={{ borderBottom: "1px solid oklch(0.24 0.04 240)" }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "oklch(0.72 0.14 75 / 15%)", border: "1px solid oklch(0.72 0.14 75 / 30%)" }}
            >
              <Phone size={15} style={{ color: "oklch(0.72 0.14 75)" }} />
            </div>
            <h2 className="text-sm font-semibold" style={{ color: "oklch(0.92 0.02 240)" }}>
              New {attachment ? "MMS" : "Text"} Message
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: "oklch(0.55 0.03 240)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "oklch(0.24 0.04 240)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* To: Phone with autocomplete */}
          <div style={{ position: "relative" }}>
            <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: "oklch(0.55 0.03 240)" }}>
              To (Phone Number) <span style={{ color: "oklch(0.65 0.18 25)" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                ref={phoneInputRef}
                type="tel"
                value={toPhone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSend(); if (e.key === "Escape") setShowSuggestions(false); }}
                onFocus={() => { if (toPhone.length >= 2) setShowSuggestions(true); }}
                placeholder="(555) 867-5309 or search by name…"
                className="w-full rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                style={{
                  background: "oklch(0.12 0.03 240)",
                  border: `1px solid ${phoneError ? "oklch(0.65 0.18 25)" : "oklch(0.28 0.04 240)"}`,
                  color: "oklch(0.92 0.02 240)",
                  paddingRight: "36px",
                }}
                autoFocus
              />
              <Search size={13} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "oklch(0.45 0.03 240)", pointerEvents: "none" }} />
            </div>
            {phoneError && (
              <p className="mt-1 text-xs" style={{ color: "oklch(0.65 0.18 25)" }}>{phoneError}</p>
            )}

            {/* Autocomplete dropdown */}
            {showSuggestions && contactSuggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 100,
                  marginTop: "4px",
                  background: "oklch(0.14 0.04 240)",
                  border: "1px solid oklch(0.28 0.04 240)",
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px oklch(0.05 0.02 240 / 60%)",
                  overflow: "hidden",
                }}
              >
                {contactSuggestions.map((contact, i) => (
                  <button
                    key={i}
                    onMouseDown={(e) => { e.preventDefault(); selectContact(contact); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                    style={{ background: "transparent", border: "none", cursor: "pointer", borderBottom: i < contactSuggestions.length - 1 ? "1px solid oklch(0.22 0.04 240)" : "none" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "oklch(0.20 0.04 240)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div
                      style={{
                        width: "30px", height: "30px", borderRadius: "50%", flexShrink: 0,
                        background: "oklch(0.72 0.14 75 / 20%)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "12px", fontWeight: 800, color: "oklch(0.72 0.14 75)",
                      }}
                    >
                      {(contact.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: "13px", fontWeight: 700, color: "oklch(0.90 0.02 240)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {contact.name}
                      </p>
                      <p style={{ fontSize: "12px", fontWeight: 500, color: "oklch(0.55 0.03 240)", margin: 0, fontFamily: "monospace" }}>
                        {formatPhoneDisplay(contact.phone)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Name / Label (optional) */}
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: "oklch(0.55 0.03 240)" }}>
              Name / Label <span className="normal-case font-normal" style={{ color: "oklch(0.45 0.03 240)" }}>(optional — for log)</span>
            </label>
            <input
              type="text"
              value={toName}
              onChange={(e) => setToName(e.target.value)}
              placeholder="e.g. John Smith, IRS Office, New Lead…"
              className="w-full rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
              style={{
                background: "oklch(0.12 0.03 240)",
                border: "1px solid oklch(0.28 0.04 240)",
                color: "oklch(0.92 0.02 240)",
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "oklch(0.72 0.14 75 / 60%)"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "oklch(0.28 0.04 240)"; }}
            />
          </div>

          {/* Message */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium uppercase tracking-wider" style={{ color: "oklch(0.55 0.03 240)" }}>
                Message
              </label>
              {!attachment && (
                <span className="text-xs" style={{ color: charCount > 320 ? "oklch(0.65 0.18 25)" : "oklch(0.45 0.03 240)" }}>
                  {charCount} chars · {segmentCount} {segmentCount === 1 ? "segment" : "segments"}
                </span>
              )}
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={attachment ? "Optional caption for your attachment…" : "Type your message here…"}
              rows={4}
              className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none transition-all"
              style={{
                background: "oklch(0.12 0.03 240)",
                border: "1px solid oklch(0.28 0.04 240)",
                color: "oklch(0.92 0.02 240)",
                lineHeight: "1.6",
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "oklch(0.72 0.14 75 / 60%)"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "oklch(0.28 0.04 240)"; }}
            />
          </div>

          {/* Attachment area */}
          <div>
            {attachment ? (
              /* Attachment preview */
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "oklch(0.12 0.03 240)", border: "1px solid oklch(0.28 0.04 240)" }}>
                {attachment.previewUrl ? (
                  <img src={attachment.previewUrl} alt="preview" className="rounded-lg" style={{ width: 52, height: 52, objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div className="flex items-center justify-center rounded-lg" style={{ width: 52, height: 52, background: "oklch(0.20 0.04 240)", flexShrink: 0 }}>
                    <FileText size={24} style={{ color: "oklch(0.72 0.14 75)" }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: "oklch(0.92 0.02 240)" }}>{attachment.file.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: "oklch(0.50 0.03 240)" }}>
                    {(attachment.file.size / 1024).toFixed(0)} KB · {attachment.file.type.split("/")[1]?.toUpperCase()} · MMS
                  </p>
                </div>
                <button
                  onClick={() => setAttachment(null)}
                  className="p-1.5 rounded-lg transition-colors"
                  style={{ color: "oklch(0.55 0.03 240)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "oklch(0.22 0.04 240)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              /* Attach button + drag hint */
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAttachment(f); e.target.value = ""; }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 w-full justify-center py-2.5 rounded-xl text-xs font-medium transition-colors"
                  style={{
                    background: "oklch(0.12 0.03 240)",
                    border: isDragOver ? "2px dashed oklch(0.55 0.18 240)" : "1px dashed oklch(0.35 0.04 240)",
                    color: "oklch(0.55 0.03 240)",
                  }}
                >
                  {isDragOver ? (
                    <><ImageIcon size={13} /> Drop to attach</>
                  ) : (
                    <><Paperclip size={13} /> Attach Image or PDF (MMS) — drag &amp; drop or click</>
                  )}
                </button>
                <p className="mt-1 text-xs text-center" style={{ color: "oklch(0.38 0.03 240)" }}>
                  JPEG, PNG, GIF, WebP, PDF · max 4.5 MB · sent via Twilio
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-4 rounded-b-2xl"
          style={{ borderTop: "1px solid oklch(0.24 0.04 240)" }}
        >
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-xl text-sm transition-colors"
            style={{ color: "oklch(0.55 0.03 240)", background: "transparent" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "oklch(0.22 0.04 240)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: canSend ? "oklch(0.72 0.14 75)" : "oklch(0.28 0.04 240)",
              color: canSend ? "oklch(0.12 0.04 240)" : "oklch(0.45 0.03 240)",
              cursor: canSend ? "pointer" : "not-allowed",
            }}
          >
            <Send size={14} />
            {uploadMmsFile.isPending ? "Uploading…" : isSending ? "Sending…" : attachment ? "Send MMS" : "Send Text"}
          </button>
        </div>
      </div>
    </div>
  );
}
